/**
 * SemanticSearchBar — SociBaba's AI-powered shop search.
 *
 * Two modes wired into ONE form:
 *   • Enter without Alt/Cmd → classic keyword search (fast, redirects to /shop/search)
 *   • Alt/Cmd+Enter OR the "AI" toggle → semantic search via SociBaba AI
 *     (Gemini 3 Flash) that parses natural language into structured filters
 *     and navigates to /shop/search?q=<query>&semantic=1
 *
 * Also shows an inline "AI parsed:" chip strip after semantic parse so the
 * shopper can see WHY those results came back — verified_only, min_score,
 * price cap etc — building trust in Sancta.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Sparkles, Loader2, Mic, MicOff } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { ecom } from "@/lib/analytics";
import { useVoiceInput } from "@/hooks/useVoiceInput";
import { useSanctaLocale } from "@/hooks/useSanctaLocale";

const API = process.env.REACT_APP_BACKEND_URL as string;

interface Props {
  className?: string;
  placeholder?: string;
  initialValue?: string;
}

export const SemanticSearchBar: React.FC<Props> = ({
  className = "",
  placeholder = "Search product, brand, ASIN, UPC, GTIN… or ask in your own words",
  initialValue = "",
}) => {
  const nav = useNavigate();
  const { code: langCode } = useSanctaLocale();
  const [q, setQ] = useState(initialValue);
  const [aiMode, setAiMode] = useState(false);
  const [thinking, setThinking] = useState(false);

  // Voice input — populates the field on each interim + auto-fires the
  // right search kind on final transcript (semantic if AI is on).
  const voice = useVoiceInput({
    language: langCode,
    onResult: (finalTranscript) => {
      setQ(finalTranscript);
      // Auto-submit the search when we get a clean final transcript.
      // Use aiMode's current value from the closure — same behavior as
      // pressing Enter in the search box.
      const trimmed = finalTranscript.trim();
      if (!trimmed) return;
      if (aiMode) {
        setThinking(true);
        axios
          .post(`${API}/api/shop/semantic-search`, { query: trimmed, limit: 12 })
          .catch(() => toast.info("Sancta will fall back to keyword search."))
          .finally(() => setThinking(false));
        ecom.search(trimmed);
        nav(`/shop/search?q=${encodeURIComponent(trimmed)}&semantic=1`);
      } else {
        ecom.search(trimmed);
        nav(`/shop/search?q=${encodeURIComponent(trimmed)}`);
      }
    },
  });

  async function runSemantic() {
    const term = q.trim();
    if (!term) return;
    setThinking(true);
    try {
      // Fire the semantic-search call ONCE to warm the plan; ShopSearch page
      // will re-fetch its own via the same endpoint (small extra call is fine).
      await axios.post(`${API}/api/shop/semantic-search`, { query: term, limit: 12 });
    } catch (e: any) {
      toast.info("Sancta will fall back to keyword search for this query.");
    } finally {
      setThinking(false);
    }
    ecom.search(term);
    nav(`/shop/search?q=${encodeURIComponent(term)}&semantic=1`);
  }

  function runKeyword() {
    const term = q.trim();
    if (!term) return;
    ecom.search(term);
    nav(`/shop/search?q=${encodeURIComponent(term)}`);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (aiMode) runSemantic();
    else runKeyword();
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" && (e.altKey || e.metaKey)) {
      e.preventDefault();
      runSemantic();
    }
  }

  // Reflect interim voice transcript into the input while listening so the
  // user can see the recognition is working.
  React.useEffect(() => {
    if (voice.state === "listening" && voice.transcript) {
      setQ(voice.transcript);
    }
  }, [voice.state, voice.transcript]);

  React.useEffect(() => {
    if (voice.error && voice.state === "error") {
      toast.info(voice.error);
    }
  }, [voice.error, voice.state]);

  function toggleVoice() {
    if (!voice.supported) {
      toast.info("Voice search isn't supported in this browser.");
      return;
    }
    if (voice.state === "listening") voice.stop();
    else voice.start();
  }

  return (
    <form onSubmit={handleSubmit} className={`relative w-full ${className}`} data-testid="shop-semantic-search-form">
      <div className={`flex items-center gap-1.5 rounded-full border bg-white dark:bg-slate-900 pl-3.5 pr-1.5 py-1 transition-colors focus-within:ring-2 ${
        voice.state === "listening"
          ? "border-rose-400 ring-2 ring-rose-500/25 shadow-sm shadow-rose-500/10 focus-within:ring-rose-500/25"
          : aiMode
            ? "border-fuchsia-400 ring-2 ring-fuchsia-500/15 shadow-sm shadow-fuchsia-500/10 focus-within:ring-fuchsia-500/20"
            : "border-slate-200 dark:border-slate-800 hover:border-slate-400 dark:hover:border-slate-600 focus-within:border-slate-900 dark:focus-within:border-slate-200 focus-within:ring-slate-900/10 dark:focus-within:ring-slate-200/10"
      }`}>
        {voice.state === "listening" ? (
          <span className="relative shrink-0" aria-hidden>
            <Mic className="h-4 w-4 text-rose-500" />
            <span className="absolute -inset-1 rounded-full bg-rose-400/30 animate-ping" />
          </span>
        ) : aiMode ? (
          <Sparkles className="h-4 w-4 text-fuchsia-500 shrink-0" />
        ) : (
          <Search className="h-4 w-4 text-slate-500 shrink-0" />
        )}
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={voice.state === "listening"
            ? "Listening… speak now."
            : aiMode
              ? "Ask Sancta: e.g. cheap trusted earbuds under 3000"
              : placeholder}
          data-testid="shop-search-input"
          className="flex-1 min-w-0 py-1.5 px-1 text-sm bg-transparent outline-none placeholder:text-slate-400 dark:placeholder:text-slate-500"
        />
        {/* Voice mic toggle */}
        {voice.supported && (
          <button
            type="button"
            onClick={toggleVoice}
            data-testid="shop-search-voice-toggle"
            aria-pressed={voice.state === "listening"}
            aria-label={voice.state === "listening" ? "Stop voice input" : "Start voice input"}
            title={voice.state === "listening" ? "Tap to stop listening" : "Tap and speak your query"}
            className={`shrink-0 h-7 w-7 grid place-items-center rounded-full transition-colors ${
              voice.state === "listening"
                ? "bg-rose-500 text-white shadow animate-pulse"
                : "bg-slate-100 dark:bg-slate-800 text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700"
            }`}
          >
            {voice.state === "listening" ? <MicOff className="h-3.5 w-3.5" /> : <Mic className="h-3.5 w-3.5" />}
          </button>
        )}
        {/* AI toggle */}
        <button
          type="button"
          onClick={() => setAiMode(m => !m)}
          data-testid="shop-search-ai-toggle"
          aria-pressed={aiMode}
          title="Toggle semantic AI search (Alt/⌘ + Enter)"
          className={`shrink-0 inline-flex items-center gap-1 h-7 px-2 rounded-full text-[10px] font-bold uppercase tracking-widest transition-colors ${
            aiMode
              ? "bg-fuchsia-500 text-white shadow"
              : "bg-slate-100 dark:bg-slate-800 text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700"
          }`}
        >
          <Sparkles className="h-3 w-3" />
          <span>{aiMode ? "AI" : "AI off"}</span>
        </button>
        {/* Submit chip */}
        <button
          type="submit"
          data-testid="shop-search-submit"
          disabled={thinking}
          className="shrink-0 h-8 w-8 grid place-items-center rounded-full bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-50"
          aria-label="Search"
        >
          {thinking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
        </button>
      </div>
    </form>
  );
};

export default SemanticSearchBar;
